<script setup>
import { openHubRoom } from '../../helpers/hubRoom';
import XlSkeleton from '../xl/XlSkeleton.vue';
import axios from 'axios';
import { addHours, format } from 'date-fns';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { CAPABILITIES, ROLES, can, hubReader } from '../../helpers/roles';
import { hubEmptyState } from '../../helpers/hubEmptyStates';
import { shortDate } from '../../helpers/usDate';
import LoadingOverlay from '../LoadingOverlay.vue';
import CancelSessionModal from '../agenda/CancelSessionModal.vue';
import DeleteOfficeHoursModal from '../agenda/DeleteOfficeHoursModal.vue';
import SessionMonths from '../agenda/SessionMonths.vue';
import SessionCardSkeleton from '../agenda/SessionCardSkeleton.vue';
import SessionCalendar from '../agenda/SessionCalendar.vue';
import SessionEventPopover from '../agenda/SessionEventPopover.vue';
import OfficeHoursDeskStrip from '../agenda/OfficeHoursDeskStrip.vue';
import { useAgendaLive } from '../../composables/useAgendaLive';
import { toViewerClock, zoneName } from '../../helpers/sessionZone';
import { viewerTimeZone } from '../../helpers/greeting';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlEmptyState from '../xl/XlEmptyState.vue';
import XlQuietNotice from '../xl/XlQuietNotice.vue';
import XlSegmented from '../xl/XlSegmented.vue';
import IconPlus from '../icons/IconPlus.vue';
import { shiftWeeks, weekWindow } from '../../helpers/calendarWeek';
import { SESSION_KINDS, kindOf } from '../../helpers/sessionKinds';
import { describeAccess } from '../../composables/useResourceAccess';
import useSimplePagination from '../../composables/useSimplePagination';
import SimplePagination from '../UI/SimplePagination.vue';
import { useAuthStore } from './../../stores/auth';

defineOptions({ name: 'AgendaView' });

const store = window.store;
const router = useRouter();
const route = useRoute();
const { userData } = useAuthStore();
const authStore = useAuthStore();
const isLoading = ref(true);
const loadFailed = ref(false);
const sessions = reactive({
  data: [],
  total: 0,
  isLoading: false
});
const attendees = ref([]);

// Delete, from the list or the calendar: the one dialog (CancelSessionModal),
// which asks a repeating session which dates go. The calendar hands in the
// date that was clicked, which is its own row.
const cancelling = ref(null);

const openRemove = (session, scope = 'one') => {
  cancelling.value = { session, scope };
};

const { pagination, from, to, setPage, setTotal } = useSimplePagination(10);

/**
 * Editing opens the session page, not the modal this page used to raise.
 *
 * The two had drifted: the modal could not say who a session's audience was,
 * what each file was for, or what the room would allow, so creating and
 * editing the same session described it in two different vocabularies.
 *
 * `scope` is the half that was quietly broken. The card has offered "Edit this
 * date" and "Edit the whole series" since recurrence landed, and both were
 * wired here to the same call with nothing to tell them apart — so choosing
 * the series edited one date. The API has taken the flag all along.
 */
// ----- Office Hours ---------------------------------------------------------

/** The desk strip at the top of the list (GET /office-hours/desk). */
const desk = ref(null);
/** Whether the hub has Office Hours at all; false offers to set one up. Null when unknown. */
const deskExists = ref(null);
// The Office Hours card is its own request. The list waits for it, so the
// card does not land above sessions already on screen and shove them down.
const deskLoaded = ref(false);
/** Varied title and meta widths for the loading cards, fixed so they do not change per render. */
const CARD_SKELETONS = [['46%', '58%'], ['38%', '64%'], ['52%', '49%'], ['41%', '61%']];
/**
 * The viewer's zone, named as it is on the days on screen — not today: a week
 * after the clocks change is GMT+1 even while today is GMT+2. A week the
 * clocks change in names both.
 */
const viewerZoneName = computed(() => {
  if (!isCalendar.value) return zoneName();
  const { from, to } = weekWindow(weekAnchor.value);
  const first = zoneName(undefined, new Date(`${from}T12:00:00`));
  const last = zoneName(undefined, new Date(`${to}T12:00:00`));

  return first === last ? first : `${first}, then ${last}`;
});
/** Today's waiting count for the Office Hours board in the popover. */
const deskWaiting = ref(null);

const loadDesk = async () => {
  try {
    const { data } = await axios.get(`${store.event_backend_api}/office-hours/desk`, { params: { tz: viewerTimeZone() } });
    desk.value = data?.data?.desk ?? null;
    deskExists.value = typeof data?.data?.exists === 'boolean' ? data.data.exists : null;
  } catch {
    desk.value = null;
    deskExists.value = null;
  } finally {
    deskLoaded.value = true;
  }
};

/**
 * Office Hours is a session like any other: Edit on its card opens the session
 * editor on today's slot (or the next), where this date or the whole series is
 * chosen. The owner's and hub staff's alone — a broker gets no Edit, and the
 * editor and the API refuse them besides.
 */
const canEditOfficeHours = computed(() => [ROLES.OWNER, ROLES.HUB_STAFF].includes(authStore.userRole));
const editOfficeHours = () => {
  const slot = desk.value?.today ?? desk.value?.next;
  if (slot?.id) openSessionEditor({ id: slot.id });
};

/**
 * "Open the room" for whoever is on today. The desk already names today's
 * slot — worked out on the hub's clock — and the room loads the session by
 * its id. Looking the slot up again by the computer's date found nothing
 * whenever that date was ahead of the hub's (an evening slot in Eastern time,
 * opened from Europe after midnight), and the button did nothing.
 */
const openDeskRoom = (today) => {
  handleJoinSession(today);
};

// Delete on the Office Hours card (calendar and list): asks first, then the
// page says the hub has no desk and offers to set one up.
const deletingDesk = ref(false);
const onDeskDeleted = async () => {
  deletingDesk.value = false;
  closePopover();
  window.loadNotification({ message: 'Office Hours deleted.' });
  await Promise.all([loadDesk(), getAgendaSessions()]);
  sessions.data = checkSessionOverlaps(sessions.data);
};

const openSessionEditor = (session, scope = 'occurrence') => {
  closePopover();

  router.push({
    name: 'Edit Session',
    params: { id: session.id },
    ...(scope === 'series' ? { query: { scope: 'series' } } : {}),
  });
};

/** The owner and hub staff: every session on the agenda is theirs to change. */
const canManageHubContent = computed(
  () => can(authStore.userRole, CAPABILITIES.MANAGE_HUB_CONTENT, authStore.capabilities)
);
/**
 * Who may put a session on: the owner, hub staff and brokers (Office Hours QA,
 * AC4). A broker's are Webinars and Video Conferences they host, for their own
 * book — the form and the API both hold them to that.
 */
const canCreateSessions = computed(
  () => can(authStore.userRole, CAPABILITIES.CONTRIBUTE_HUB_CONTENT, authStore.capabilities)
);

/**
 * Edit and Cancel, for one session: staff on any, a broker on the ones they
 * created and nothing else — the hub's sessions and other brokers' read the
 * same as before, with only the way in.
 */
const managesSession = (session) => {
  if (canManageHubContent.value) return true;
  if (!canCreateSessions.value || !session) return false;

  return session.video_call_type !== 'office_hours'
    && session.created_by != null
    && Number(session.created_by) === Number(viewerLocalId.value);
};

// Office Hours by the desk's own rule (canEditOfficeHours): the owner and hub
// staff, never a broker — the same as Edit on the Office Hours card above.
const canEditSession = (session) => (session?.video_call_type === 'office_hours'
  ? canEditOfficeHours.value
  : managesSession(session) || session?.can_edit === true);

const onDeleted = async ({ message } = {}) => {
  cancelling.value = null;
  window.loadNotification({ message: message || 'Session deleted.' });
  await getAgendaSessions();
  sessions.data = checkSessionOverlaps(sessions.data);
};

const calculateEventDifferenceInHours = (startDate, endDate) => {
  const startDateTime = new Date(startDate);
  const endDateTime = new Date(endDate);

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = endDateTime - startDateTime;

  // Convert the difference in milliseconds to total minutes and hours
  const totalMinutes = differenceInMilliseconds / 60000;
  const totalHours = totalMinutes / 60;

  // Calculate whole hours and remaining minutes
  const hours = Math.floor(totalHours);
  const minutes = Math.floor(totalMinutes % 60);

  let eventDifferenceInHours = '';

  // Determine the format of the output string
  if (hours > 0) {
    eventDifferenceInHours = `${hours}hr`;
    if (minutes > 0) {
      eventDifferenceInHours += ` ${minutes}min`;
    }
  } else {
    eventDifferenceInHours = `${minutes}min`;
  }

  return eventDifferenceInHours;
};

const formatEventStartDate = (dateString) => shortDate(dateString, { time: true });

const openCreateSession = () => router.push({ name: 'Create Session' });

/**
 * List or Calendar.
 *
 * Kept in localStorage because it is a layout preference, not a data scope —
 * the line stores/brokerScope.js draws when it explains why a *scope* must
 * never outlive a session. The same key pattern HubShell.vue uses for the
 * collapsed sidebar.
 *
 * Every read and write is guarded: a private window, or blocked site data,
 * makes the accessor throw rather than return nothing.
 */
const SESSION_VIEW_KEY = 'hub.sessions.view';

const storedView = () => {
  try {
    return localStorage.getItem(SESSION_VIEW_KEY) === 'Calendar' ? 'Calendar' : 'List';
  } catch {
    return 'List';
  }
};

/**
 * A view asked for by the link that brought you (?view=calendar — the
 * Overview's Calendar button), which wins over the remembered one: the button
 * said Calendar, so landing on the List needed a second click. It is then
 * remembered like a click on the toggle, and leaves the address (onMounted).
 */
const askedView = () => ({ calendar: 'Calendar', list: 'List' }[String(route.query?.view ?? '').toLowerCase()] ?? null);

const sessionView = ref(askedView() ?? storedView());
if (askedView()) {
  try {
    localStorage.setItem(SESSION_VIEW_KEY, sessionView.value);
  } catch {
    // Shown either way; only not remembered.
  }
}
const isCalendar = computed(() => sessionView.value === 'Calendar');

/** Any date inside the week on show. */
const weekAnchor = ref(new Date());
const calendarGrid = ref(null);

// One board, so a second can never be open beside the first.
const popover = reactive({ open: false, anchor: null, session: null, slot: null });

/**
 * The signed-in person as the agenda knows them.
 *
 * Sessions carry LOCAL user ids; the SSO id the auth store holds is a
 * different number, and comparing the two would tell a host they are only
 * watching. The page already resolves one to the other for its dropdowns.
 */
const viewerLocalId = computed(() => userData?.subdomain_user_id ?? null);

/**
 * The line under the page's name, which says something different depending on
 * what is being shown.
 *
 * "Repeating" and "limited" are only said when the page holds the whole
 * agenda: counted over one page of many they would be a smaller number than
 * the truth, stated as though it were the truth.
 */
const agendaSummary = computed(() => {
  if (isCalendar.value) {
    const count = sessions.data.filter((session) => kindOf(session) !== SESSION_KINDS.OFFICE_HOURS).length;

    return `${count} ${count === 1 ? 'session' : 'sessions'} this week, plus Office Hours.`
      + ' Click anything to open it, or an empty slot to add one.';
  }

  const total = sessions.total;
  const coming = `${total} ${total === 1 ? 'session' : 'sessions'} coming up`;

  if (sessions.data.length < total) return coming;

  const repeating = sessions.data.filter((session) => session.series_id).length;
  const limited = sessions.data.filter((session) => describeAccess(session.audience).kind !== 'all').length;

  return [
    coming,
    repeating ? `${repeating} repeating` : null,
    limited ? `${limited} limited to certain people` : null,
  ].filter(Boolean).join(' · ');
});

const setSessionView = async (view) => {
  if (sessionView.value === view) return;

  sessionView.value = view;
  closePopover();

  try {
    localStorage.setItem(SESSION_VIEW_KEY, view);
  } catch {
    // A remembered view is a convenience; not being able to remember it is
    // not a reason to refuse to change it.
  }

  sessions.isLoading = true;
  await getAgendaSessions(1);
};

const loadAttendees = async () => {
  try {
    const { data } = await axios.get(`${store.event_backend_api}/users`);
    if (Array.isArray(data?.registered?.data)) attendees.value = data.registered.data;
  } catch (error) {
    console.error(error);
  }
};

/**
 * Bumped by every fetch. The page asks again on a view switch, a week move,
 * a page change, a live update and every return from the editor, and the
 * answers do not come back in the order they were asked: only the newest may
 * draw, or a slow week (every date, no series folded) lands on the list.
 */
let agendaRequest = 0;

const agendaParams = (page) => (isCalendar.value
  ? { page: 1, ...weekWindow(weekAnchor.value), tz: viewerTimeZone() }
  : { page, kind: 'sessions', collapse_series: 1, upcoming: 1, tz: viewerTimeZone() });

const toSpeaker = (speaker, sessionId) => {
  if (speaker.id) return { ...speaker, sessionId };

  return {
    id: null,
    value: null,
    email: speaker,
    fullname: speaker,
    profile_image: 'invited',
    sessionId,
  };
};

const toAgendaRow = (session) => ({
  ...session,
  moderator: attendees.value.find(attendee => session.session_moderator === attendee.id) || null,
  formatted_start_date: formatEventStartDate(session.start_date),
  session_duration: calculateEventDifferenceInHours(session.start_date, session.end_date),
  session_speakers: [...(session.session_speakers ?? []), ...(session.session_invited_speakers ?? [])]
    .map(speaker => toSpeaker(speaker, session.id)),
});

const drawAgenda = (body) => {
  const events = (body?.data ?? []).map((event) => toViewerClock(event)).map(toAgendaRow);
  sessions.total = body?.meta?.total ?? 0;
  setTotal(sessions.total);
  sessions.data = checkSessionOverlaps(events);
};

const getAgendaSessions = async (page = 1) => {
  const ticket = ++agendaRequest;

  try {
    // A week is asked for, not paged to: the grid draws seven days, so it
    // fetches seven days rather than walking the whole agenda to find them.
    // The list leaves Office Hours to its card, which opens its own page;
    // the calendar draws both.
    // Every request says the viewer's zone: the week, and "today", are
    // theirs (XN-4673 AC 3).
    const res = await axios.get(`${store.event_backend_api}/agenda-events`, { params: agendaParams(page) });
    if (ticket !== agendaRequest) return;

    drawAgenda(res.data);
    loadFailed.value = false;

    loadAttendees();
  } catch (error) {
    if (ticket !== agendaRequest) return;
    console.error(error);
    // "No sessions yet" over a failed request tells somebody with a full
    // agenda that it is empty. Say it did not load, and offer to try again.
    loadFailed.value = true;
  } finally {
    if (ticket === agendaRequest) {
      isLoading.value = false;
      sessions.isLoading = false;
    }
  }
};

const everScheduled = ref(null);
const listEmpty = computed(() => !isCalendar.value && !isLoading.value && !sessions.isLoading && !loadFailed.value && !sessions.data.length);
const sessionsEmpty = computed(() => listEmpty.value && everScheduled.value !== null);

let historyRequested = false;

const loadEverScheduled = async () => {
  historyRequested = true;
  try {
    const { data } = await axios.get(`${store.event_backend_api}/agenda-events`, {
      params: { kind: 'sessions', per_page: 1, page: 1, tz: viewerTimeZone() },
    });
    everScheduled.value = (data?.meta?.total ?? 0) > 0;
  } catch {
    everScheduled.value = true;
  }
};

watch(listEmpty, (empty) => {
  if (empty && !historyRequested) loadEverScheduled();
}, { immediate: true });

const deckInput = () => {
  const today = desk.value?.today;

  return {
    reader: hubReader(authStore),
    role: authStore.userRole,
    capabilities: authStore.capabilities,
    everScheduled: everScheduled.value,
    hasDesk: Boolean(desk.value),
    canJoinDesk: Boolean(today && (today.open || today.you_are_on)),
  };
};

const firstRun = computed(() => (sessionsEmpty.value ? hubEmptyState('sessions', deckInput()) : null));
const quietWeek = computed(() => (sessionsEmpty.value ? hubEmptyState('quietWeek', deckInput()) : null));

const EMPTY_ACTIONS = {
  schedule: () => openCreateSession(),
  'office-hours': () => openDeskRoom(desk.value?.today),
  'office-hours-setup': () => router.push({ name: 'Create Session', query: { format: SESSION_KINDS.OFFICE_HOURS } }),
};
const onEmptyAction = (id) => EMPTY_ACTIONS[id]?.();

/* ---- the week grid, and the board that opens beside it ---- */

const closePopover = () => {
  popover.open = false;
  popover.session = null;
  popover.slot = null;
};

const showWeek = async (anchor) => {
  weekAnchor.value = anchor;
  closePopover();
  sessions.isLoading = true;
  await getAgendaSessions(1);
};

const handleWeekMove = (by) => showWeek(shiftWeeks(weekAnchor.value, by));
const handleWeekToday = () => showWeek(new Date());

/**
 * Opened at its final size (Office Hours → "the card jumps when it opens"):
 * today's desk shows who is waiting, and that count arrived after the card had
 * opened, so the card grew under the reader. It is asked first — never for
 * longer than a moment, so a slow answer cannot hold the card back.
 */
const WAITING_WAIT_MS = 400;
const openSessionBoard = async ({ session, event }) => {
  // Read now: the event's target is gone once anything has been awaited.
  const anchor = event?.currentTarget ?? null;
  const waiting = loadWaitingFor(session);
  if (deskWaitingAsked(session)) {
    await Promise.race([waiting, new Promise((resolve) => { setTimeout(resolve, WAITING_WAIT_MS); })]);
  }
  popover.session = session;
  popover.slot = null;
  popover.anchor = anchor;
  popover.open = true;
};

const deskWaitingAsked = (session) => kindOf(session) === SESSION_KINDS.OFFICE_HOURS
  && new Date(String(session.start_date).replace(' ', 'T')).toDateString() === new Date().toDateString();

/** Only today's desk has anybody waiting; anything else asks nothing. */
const loadWaitingFor = async (session) => {
  deskWaiting.value = null;
  if (kindOf(session) !== SESSION_KINDS.OFFICE_HOURS) return;

  const start = new Date(String(session.start_date).replace(' ', 'T'));
  const today = new Date();
  if (start.toDateString() !== today.toDateString()) return;

  try {
    const { data } = await axios.get(`${store.event_backend_api}/office-hours/${session.id}/waiting`);
    // Asked before the card opens, so the card may not be this session's yet:
    // only a later click on another one discards the answer.
    if (!popover.open || popover.session?.id === session.id) deskWaiting.value = data?.data?.waiting ?? null;
  } catch {
    deskWaiting.value = null;
  }
};

const openSlotBoard = ({ day, hour, event }) => {
  // Clicking empty space creates a session.
  if (!canCreateSessions.value) return;

  popover.slot = { day, hour };
  popover.session = null;
  popover.anchor = event?.currentTarget ?? null;
  popover.open = true;
};

/**
 * Entering a room, in the hub: it opens over this page (HubSessionRoom), so
 * staff never land in the policyholder portal, and closing it leaves the list
 * as it was — the same tab, filter and scroll. The host, staff and speakers go
 * straight in; the join dialog and the waiting room are a policyholder's.
 */
const handleJoinSession = (session) => {
  if (!session?.id) return;
  closePopover();

  openHubRoom(session.id);
};

/** An empty cell already knows the day and the hour it was clicked on. */
/**
 * An empty slot on the calendar opens the create page, not the old modal.
 *
 * The board's own copy for this board says "you can change it on the next
 * screen" — the next screen being the eight-step form, which is where every
 * other route into creating a session now goes. Landing in the legacy modal
 * instead left two different ways to make a session depending on where you
 * clicked.
 *
 * The slot travels as query parameters rather than as state, so the page can
 * be reloaded or linked to and still open on the hour that was clicked.
 */
const handleCreateInSlot = ({ day, hour }) => {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0);
  const end = addHours(start, 1);

  closePopover();

  router.push({
    name: 'Create Session',
    // The slot is the viewer's 2 PM; the form converts it into the zone the
    // session is scheduled in (XN-4673).
    query: {
      date: format(start, 'yyyy-MM-dd'),
      from: format(start, 'HH:mm'),
      to: format(end, 'HH:mm'),
      tz: viewerTimeZone(),
    },
  });
};

const handlePrevious = async () => {
  setPage(pagination.currentPage - 1);
  sessions.isLoading = true;
  await getAgendaSessions(pagination.currentPage);
};

const handleNext = async () => {
  setPage(pagination.currentPage + 1);
  sessions.isLoading = true;
  await getAgendaSessions(pagination.currentPage);
};

const checkSessionOverlaps = (sessions) => {
  return sessions.map(session => {
    const isOverlapping = sessions.some(otherSession => {
      return (
        session.status !== 'cancelled' &&
        otherSession.status !== 'cancelled' &&
        session.session_moderator &&
        otherSession.session_moderator &&
        session.session_moderator.id === otherSession.session_moderator.id &&
        session.id !== otherSession.id &&
        (
          new Date(session.start_date) <= new Date(otherSession.end_date) &&
          new Date(session.end_date) >= new Date(otherSession.start_date)
        )
      );
    });
    return {
      ...session,
      isOverlapping
    };
  });
};

onMounted(async () => {
  // A link into a room (a reminder email, an old portal bookmark) arrives as
  // ?room=: it opens over this list, in the hub, and leaves the address.
  const linkedRoom = Number(route.query?.room);
  if (linkedRoom) openHubRoom(linkedRoom);

  // Both leave the address: the room is open, and the view is in place — a
  // later toggle must not be undone by a reload.
  if (linkedRoom || route.query?.view) {
    const rest = { ...route.query };
    delete rest.room;
    delete rest.view;
    router.replace({ query: rest });
  }

  loadDesk();
  await getAgendaSessions();
});

// A covered day or a moved session shows up without a reload.
useAgendaLive(() => {
  loadDesk();
  getAgendaSessions(isCalendar.value ? 1 : (pagination.currentPage ?? 1));
});
</script>

<template>
  <div class="page-content">

    <CancelSessionModal
      v-if="cancelling"
      :session="cancelling.session"
      :initial-scope="cancelling.scope"
      @close="cancelling = null"
      @deleted="onDeleted"
    />

    <!-- Northstar's tokens, which this page was built to and never opted into.
         Without the flag every XL component here falls back to Generation 1:
         #F5D9FF for a chip that should be #F5EFF9, #745186 at the top of a
         button gradient that should be #8154A6, #E0E0E0 for a card border
         that should be #EDE7F0. The components were right; the palette
         underneath them was the old one. -->
    <div class="page-content__inner" data-xl-gen="northstar">
      <div class="page-content__inner__header">
        <div>
          <!-- The shell's top bar names the page; this is the card's own lead-in. -->
          <h2>Sessions</h2>
          <hr />
          <!-- Not a count until there is one: "0 sessions coming up" over a
               loading list read as an empty agenda (QA). Its own line's
               height, so nothing moves when the number lands. -->
          <h5 data-testid="agenda-summary">
            <XlSkeleton v-if="isLoading" width="260px" height="0.9em" radius="6px" class="agenda-summary-sk" data-testid="agenda-summary-loading" />
            <template v-else>{{ agendaSummary }}</template>
          </h5>
          <!-- Which clock the page is on: times follow the viewer, so say whose. -->
          <p class="agenda-zone" data-testid="agenda-zone">Times are shown in your time zone — {{ viewerZoneName }}</p>
        </div>

        <div class="agenda-controls">
          <!-- Two ways of reading the same agenda. The choice is remembered,
               because coming back to the list you did not choose is a small
               irritation repeated every visit. -->
          <xl-segmented
            :model-value="sessionView"
            :options="['List', 'Calendar']"
            aria-label="How to show the agenda"
            data-testid="agenda-view"
            @update:model-value="setSessionView"
          />

          <xl-button v-if="canCreateSessions" data-testid="agenda-create" @click="openCreateSession">
            <IconPlus color="currentColor" size="16" />
            Create a session
          </xl-button>
        </div>
      </div>

      <div class="page-content__inner__body">
        <!--
          The list as it will be — the Office Hours card, a month, its session
          cards — with placeholders where the words go (Sessions: a generic
          rows skeleton, then the desk card arriving late and pushing the list
          down). The calendar needs none: its empty week is drawn at once and
          fills in, under the progress line.
        -->
        <div v-if="!isCalendar && (isLoading || !deskLoaded || (listEmpty && everScheduled === null))" class="agenda-loading" aria-busy="true" data-testid="agenda-loading">
          <!-- Laid out as the list is (measured, Sessions → "the page jumps as
               it loads"): the desk card, then the month with its cards, in
               the list's own 24px column. The desk's place only while it may
               still come, or when it has. -->
          <XlSkeleton v-if="!deskLoaded || desk || (deskExists === false && canEditOfficeHours)" width="100%" height="114px" radius="20px" class="agenda-loading__desk" />
          <div class="agenda-loading__months">
            <div class="agenda-loading__month">
              <XlSkeleton width="84px" height="0.8em" radius="6px" class="agenda-loading__sk" />
              <span class="agenda-loading__rule" />
            </div>
            <session-card-skeleton v-for="[title, meta] in CARD_SKELETONS" :key="title" :title-width="title" :meta-width="meta" />
          </div>
        </div>
        <div v-else-if="loadFailed && !sessions.data.length" class="page-content__inner__body--no-data" role="alert" data-testid="agenda-load-error">
          <h4>The sessions couldn't be loaded.</h4>
          <xl-button variant="ghost" data-testid="agenda-retry" @click="sessions.isLoading = true; getAgendaSessions(1)">Try again</xl-button>
        </div>
        <!--
          The calendar stays up whether or not the week has anything in it.
          In calendar mode the fetch is scoped to the week on show, so a quiet
          week returns nothing and this used to fall through to the empty
          state — which took the week arrows with it and left no way back to a
          week that did have sessions, short of reloading. An empty week is a
          thing the calendar can draw; it is not the agenda being empty.
        -->
        <div v-else-if="isCalendar || sessions.data.length || desk || sessionsEmpty" class="page-content__inner__body--data">
          <loading-overlay v-if="sessions.isLoading || isLoading" style="border-radius: 12px;" />
          <!--
            The month-grouped cards the design board draws. The page keeps the
            fetch, the modals and the pagination; the list owns only which card
            is open, which is what keeps "one at a time" true.
          -->
          <!-- The desk on the list; on either view, once it is deleted, the
               line saying there is none (unless the first-run card already
               offers to set one up). -->
          <office-hours-desk-strip
            v-if="!isCalendar || (!desk && deskExists === false)"
            :desk="isCalendar ? null : desk"
            :exists="firstRun ? null : deskExists"
            :can-edit="canEditOfficeHours"
            @open-room="openDeskRoom"
            @edit="editOfficeHours"
            @delete="deletingDesk = true"
            @set-up="onEmptyAction('office-hours-setup')"
          />

          <xl-card v-if="isCalendar" ref="calendarGrid" class="agenda-calendar">
            <session-calendar
              :sessions="sessions.data"
              :anchor="weekAnchor"
              :open-id="popover.session?.id ?? null"
              @move="handleWeekMove"
              @today="handleWeekToday"
              @open="openSessionBoard"
              @new-slot="openSlotBoard"
            />
          </xl-card>

          <XlEmptyState
            v-if="firstRun"
            variant="page"
            :icon="firstRun.icon"
            :headline="firstRun.headline"
            :body="firstRun.body"
            :actions="firstRun.actions"
            data-testid="agenda-first-run"
            @action="onEmptyAction"
          />
          <XlQuietNotice
            v-else-if="quietWeek"
            :headline="quietWeek.headline"
            :body="quietWeek.body"
            :actions="quietWeek.actions"
            data-testid="agenda-quiet-week"
            @action="onEmptyAction"
          />

          <session-months
            v-if="!isCalendar"
            :sessions="sessions.data"
            :can-manage="managesSession"
            :can-edit="canEditSession"
            @join="handleJoinSession"
            @edit="session => openSessionEditor(session)"
            @cancel="session => openRemove(session)"
          />
        </div>

      </div>

      <!--
        Pages belong to the list. The calendar moves a week at a time from its
        own arrows, and a second set of prev/next in the corner only invites
        the question of which one moves what.
      -->
      <simple-pagination
        v-if="sessions.data.length && !isCalendar"
        :pagination="pagination"
        :from="from"
        :to="to"
        class="page-content__inner__footer"
        @previous="handlePrevious"
        @next="handleNext"
      />
    </div>

    <session-event-popover
      v-model:open="popover.open"
      :anchor="popover.anchor"
      :boundary="calendarGrid?.$el ?? calendarGrid"
      :session="popover.session"
      :empty-slot="popover.slot"
      :viewer-id="viewerLocalId"
      :can-manage="managesSession(popover.session)"
      :can-edit="canEditSession(popover.session)"
      :waiting="deskWaiting"
      @join="handleJoinSession"
      @edit="session => openSessionEditor(session)"
      @cancel="session => openRemove(session)"
      @delete-desk="deletingDesk = true"
      @create="handleCreateInSlot"
    />
    <DeleteOfficeHoursModal v-if="deletingDesk" :waiting="deskWaiting ?? desk?.today?.waiting ?? 0" @close="deletingDesk = false" @deleted="onDeskDeleted" />
  </div>
</template>

<style lang="scss" scoped>
/* The summary's placeholder sits on the line the count will. */
.agenda-summary-sk { width: 260px; max-width: 100%; vertical-align: middle; }

/* SessionMonths' month heading and OfficeHoursDeskStrip's place, kept in step with them. */
/* The list's own column (.page-content__inner__body--data): a flex column 24px
   apart, so the desk's margin and the month's do not collapse into one. */
.agenda-loading { display: flex; flex-direction: column; row-gap: 24px; }
.agenda-loading__desk { display: block; margin-bottom: 18px; }
/* SessionMonths' heading: 34px above, 14px below, its line 17px. */
.agenda-loading__month { display: flex; align-items: center; gap: 12px; margin: 34px 0 14px; height: 17px; }
.agenda-loading__sk { width: auto; flex: 0 0 auto; }
.agenda-loading__rule { flex: 1; height: 1px; background: var(--xl-color-border); }

.agenda-zone {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: var(--xl-color-secondary-text, #79747E);
}

/* The header's right-hand side: how to read the agenda, and the one thing to
   do to it. */
.agenda-controls {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

/* XlCard carries the border, radius and shadow; only the padding is this
   card's own — a week grid wants less room around it than a card of text. */
.agenda-calendar { padding: 16px 18px; }

.page-content {
  position: relative;
  padding-bottom: var(--setup-page-padding) !important;

  .modal--agenda {
    background-color: #27262573;
    z-index: 100;
  }

  &__inner {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: #fff;
    border-radius: 12px;
    padding: 24px 24px 24px 24px;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      column-gap: 24px;
      margin-bottom: 24px;

      >div:first-child {
        flex: 1;
      }

      h2 {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        line-height: 32px;
      }

      hr {
        display: block;
        height: 1px;
        border: 0 !important;
        border-top: 1px solid #808080 !important;
        margin-bottom: 10px;
        padding: 0;
      }

      h5 {
        margin: 0;
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
        color: #49454F
      }
    }

    &__body {
      flex: 1;

      &--data {
        display: flex;
        flex-direction: column;
        row-gap: 24px;
        justify-content: center;

        &--create {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        table {
          border-collapse: collapse;
          margin: 0 -24px;

          thead {
            tr {
              th {
                text-align: left;
                font-size: 14px;
                line-height: 20px;
                color: #1D1B20;
                font-weight: 700;
                padding: 16px 0;
                padding-left: 16px;
                white-space: nowrap;
              }

              th:first-child {
                padding-left: 24px;
              }

              th:last-child {
                padding-right: 24px;
              }
            }
          }

          tbody {
            tr {
              transition: background-color 150ms linear;
              cursor: pointer;

              &:hover {
                background-color: #5E2F7814;
              }

              td:first-child {
                width: 40px;

                .caret {
                  display: flex;
                  width: 32px;
                  height: 32px;
                  padding: 4px;
                  border-radius: 50%;
                  cursor: pointer;

                  svg {
                    transition: transform 150ms ease-in-out;
                  }

                  .down {
                    transform: rotate(0deg);
                  }

                  .up {
                    transform: rotate(-180deg);
                  }

                  &:hover {
                    background-color: #1d1b2014;
                  }
                }
              }

              td:nth-child(2) {
                width: 128px;
              }

              td:nth-child(3) {
                width: 88px;
              }

              td:nth-child(4) {
                width: 80px;
              }

              td:nth-child(6) {
                width: 184px;
              }

              td:nth-child(7) {
                width: 160px;
              }

              td:nth-child(8) {
                width: 80px;
              }

              td {
                position: relative;
                padding: 16px 0;
                padding-left: 16px;
                vertical-align: top;

                &:after {
                  position: absolute;
                  content: '';
                  border-bottom: 1px solid #CEC3CE;
                  width: 100%;
                  transform: translateX(-50%);
                  bottom: 0;
                  left: 50%;
                }

                .session-title {
                  position: relative;
                }

                img,
                .default-avatar {
                  border-radius: 50%;
                  height: 20px;
                  width: 20px;
                }

                .overlapping-warning {
                  display: flex;
                  flex-direction: column;
                  justify-content: space-around;

                  img {
                    margin: 4px;
                  }
                }

                .speaker-name {
                  font-size: 14px;
                  line-height: 20px;
                  color: #1D1B20;
                  overflow-wrap: anywhere;
                }

                .column-center {
                  display: flex;
                  flex-direction: column;
                  row-gap: 8px;
                }

                .row-center {
                  display: flex;
                  column-gap: 8px;
                  align-items: center;
                  height: 100%;

                  .draft-warning {
                    display: flex;
                    padding: 4px;
                    border-radius: 50%;

                    &:hover {
                      background-color: #5E2F7814;
                      transition: background-color 150ms linear;
                    }
                  }
                }

                .description-container {
                  display: grid;
                  transition: grid-template-rows 0.2s linear;

                  .description {
                    font-size: 14px;
                    line-height: 16px;
                    color: #49454F;
                    overflow: hidden;
                  }
                }

                .plus-speakers-container {
                  display: grid;
                  transition: grid-template-rows 0.2s linear;

                  >div {
                    overflow: hidden;
                  }
                }

                .plus-speakers {
                  align-self: flex-end;
                  font-size: 14px;
                  line-height: 20px;
                }
              }

              td:first-child {
                padding-left: 24px;

                &:after {
                  margin-left: 24px;
                }
              }

              td:last-child {
                padding-right: 24px;

                &:after {
                  width: calc(100% - 24px);
                  margin-left: -24px;
                }
              }

              td.span-1 {
                width: 16.67%;
              }

              td {

                .actions {
                  display: flex;
                  column-gap: 6px;
                  justify-content: flex-end;

                  span {
                    display: flex;
                    width: 32px;
                    height: 32px;
                    padding: 6px;
                    border-radius: 50%;
                    cursor: pointer;
                  }

                  span:hover {
                    background-color: #5E2F7814;
                  }
                }

                .session-content-cell {
                  display: flex;
                  align-items: center;
                  column-gap: 4px;
                }
              }
            }
          }
        }
      }

      &--no-data {
        display: flex;
        flex-direction: column;
        row-gap: 14px;
        justify-content: center;
        align-items: center;
        height: 100%;

        h4 {
          margin: 0;
          font-size: 16px;
          line-height: 24px;
          font-weight: 400;
        }
      }
    }

    &__footer {
      position: sticky;
      bottom: 10px;
      width: max-content;
      margin-left: auto;
      margin-top: auto;
    }
  }
}

.modal--agenda {
  .c-modal__title {
    span {
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
      text-align: left;
      color: #1E1A1F;
    }
  }
}
</style>
